import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus, ShoppingBag } from "@phosphor-icons/react";
import { toast } from "sonner";

const SPLIT_IMG = "https://customer-assets.emergentagent.com/job_exfoliating-bars/artifacts/8tud5x1e_handsoap.png";
const TOTAL_IMG = "https://customer-assets.emergentagent.com/job_exfoliating-bars/artifacts/cmoaqeto_7e7961233_totalscrubbar.png";

const PRODUCTS = [
  {
    id: "split_scrub",
    name: "Split Scrub Bar",
    tagline: "Scrub + Soothe",
    shortDesc: "Treat your skin to the ultimate pampering experience — serious exfoliation meets nourishing hydration. Clean, polished, and irresistibly smooth.",
    image: SPLIT_IMG,
  },
  {
    id: "total_scrub",
    name: "Total Scrub Bar",
    tagline: "All Grit. No Quit.",
    shortDesc: "Full charcoal and beach sand throughout for maximum exfoliation.",
    image: TOTAL_IMG,
  },
];

const PACKS = [
  { id: "pack_1", name: "Single Bar", barCount: 1, price: 8, label: "1 Bar", savings: null },
  { id: "pack_2", name: "2-Pack", barCount: 2, price: 15, label: "2 Bars", savings: "Save $1" },
  { id: "pack_3", name: "3-Pack", barCount: 3, price: 20, label: "3 Bars", savings: "Save $4" },
  { id: "pack_5", name: "5-Pack", barCount: 5, price: 30, label: "5 Bars", savings: "Save $10" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

function PackCard({ pack, onSelect, isSelected }) {
  return (
    <button
      onClick={() => onSelect(pack)}
      className={`relative w-full p-6 border text-left transition-all duration-300 ${
        isSelected ? "border-[#FF5A36] ring-1 ring-[#FF5A36]" : "border-[#2A2A2A] hover:border-[#444]"
      }`}
      style={{ backgroundColor: isSelected ? '#1A1612' : '#141414' }}
      data-testid={`pack-card-${pack.id}`}
    >
      {pack.savings && (
        <span
          className="absolute top-0 right-0 px-3 py-1 text-xs font-accent font-bold tracking-wider uppercase text-white"
          style={{ backgroundColor: '#FF5A36' }}
          data-testid={`pack-savings-${pack.id}`}
        >
          {pack.savings}
        </span>
      )}
      <p className="font-accent text-xs tracking-[0.2em] uppercase mb-2" style={{ color: '#9A9A9A' }}>
        {pack.label}
      </p>
      <p className="font-heading text-3xl font-light" style={{ color: '#F0EDE5' }}>
        ${pack.price}
      </p>
      <p className="font-body text-xs mt-1" style={{ color: '#666' }}>
        ${(pack.price / pack.barCount).toFixed(2)}/bar
      </p>
    </button>
  );
}

function BarSelector({ pack, onAddToCart }) {
  const [splitCount, setSplitCount] = useState(pack.barCount === 1 ? 0 : Math.floor(pack.barCount / 2));
  const totalCount = pack.barCount - splitCount;

  const adjustSplit = (delta) => {
    const newVal = splitCount + delta;
    if (newVal >= 0 && newVal <= pack.barCount) {
      setSplitCount(newVal);
    }
  };

  const handleAdd = () => {
    const bars = [];
    if (splitCount > 0) bars.push({ id: "split_scrub", name: "Split Scrub Bar", quantity: splitCount, image: SPLIT_IMG });
    if (totalCount > 0) bars.push({ id: "total_scrub", name: "Total Scrub Bar", quantity: totalCount, image: TOTAL_IMG });

    onAddToCart({
      packId: pack.id,
      packName: pack.name,
      price: pack.price,
      barCount: pack.barCount,
      bars,
    });
    toast.success(`${pack.name} added to cart!`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-6 p-6 border"
      style={{ borderColor: '#2A2A2A', backgroundColor: '#111' }}
      data-testid="bar-selector"
    >
      <p className="font-accent text-sm font-semibold tracking-wide uppercase mb-5" style={{ color: '#F0EDE5' }}>
        Choose Your {pack.barCount === 1 ? "Bar" : "Bars"}
      </p>

      {/* Split Scrub Counter */}
      <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#2A2A2A' }}>
        <div className="flex items-center gap-4">
          <img src={SPLIT_IMG} alt="Split Scrub" className="w-12 h-12 object-cover" />
          <div>
            <p className="font-body text-sm font-medium" style={{ color: '#F0EDE5' }}>Split Scrub Bar</p>
            <p className="font-body text-xs" style={{ color: '#666' }}>Exfoliate + Moisturize</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => adjustSplit(-1)}
            disabled={splitCount <= 0}
            className="w-8 h-8 flex items-center justify-center border transition-colors hover:bg-[#FF5A36] hover:border-[#FF5A36] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#2A2A2A]"
            style={{ borderColor: '#2A2A2A', color: '#F0EDE5' }}
            data-testid="split-decrease"
          >
            <Minus size={14} weight="bold" />
          </button>
          <span className="font-accent text-base font-semibold w-6 text-center" style={{ color: '#F0EDE5' }} data-testid="split-count">
            {splitCount}
          </span>
          <button
            onClick={() => adjustSplit(1)}
            disabled={splitCount >= pack.barCount}
            className="w-8 h-8 flex items-center justify-center border transition-colors hover:bg-[#FF5A36] hover:border-[#FF5A36] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#2A2A2A]"
            style={{ borderColor: '#2A2A2A', color: '#F0EDE5' }}
            data-testid="split-increase"
          >
            <Plus size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Total Scrub Counter */}
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-4">
          <img src={TOTAL_IMG} alt="Total Scrub" className="w-12 h-12 object-cover" />
          <div>
            <p className="font-body text-sm font-medium" style={{ color: '#F0EDE5' }}>Total Scrub Bar</p>
            <p className="font-body text-xs" style={{ color: '#666' }}>Full Exfoliation</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => adjustSplit(1)}
            disabled={totalCount <= 0}
            className="w-8 h-8 flex items-center justify-center border transition-colors hover:bg-[#FF5A36] hover:border-[#FF5A36] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#2A2A2A]"
            style={{ borderColor: '#2A2A2A', color: '#F0EDE5' }}
            data-testid="total-decrease"
          >
            <Minus size={14} weight="bold" />
          </button>
          <span className="font-accent text-base font-semibold w-6 text-center" style={{ color: '#F0EDE5' }} data-testid="total-count">
            {totalCount}
          </span>
          <button
            onClick={() => adjustSplit(-1)}
            disabled={totalCount >= pack.barCount}
            className="w-8 h-8 flex items-center justify-center border transition-colors hover:bg-[#FF5A36] hover:border-[#FF5A36] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#2A2A2A]"
            style={{ borderColor: '#2A2A2A', color: '#F0EDE5' }}
            data-testid="total-increase"
          >
            <Plus size={14} weight="bold" />
          </button>
        </div>
      </div>

      <button
        onClick={handleAdd}
        className="w-full mt-5 inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-accent font-semibold tracking-wider uppercase text-white transition-all duration-300 hover:brightness-110"
        style={{ backgroundColor: '#FF5A36' }}
        data-testid="add-pack-to-cart"
      >
        <ShoppingBag size={18} weight="bold" />
        Add {pack.name} — ${pack.price}
      </button>
    </motion.div>
  );
}

export default function ShopSection({ addToCart }) {
  const [selectedPack, setSelectedPack] = useState(null);

  const handleAddToCart = (packOrder) => {
    addToCart({
      id: `${packOrder.packId}_${Date.now()}`,
      packId: packOrder.packId,
      packName: packOrder.packName,
      name: packOrder.packName,
      price: packOrder.price,
      barCount: packOrder.barCount,
      bars: packOrder.bars,
      quantity: 1,
      image: packOrder.bars[0]?.image || SPLIT_IMG,
    });
    setSelectedPack(null);
  };

  return (
    <section id="products" className="py-24 lg:py-32" data-testid="shop-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20"
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="font-accent text-sm tracking-[0.25em] uppercase mb-4"
            style={{ color: '#FF5A36' }}
          >
            The Collection
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight"
            style={{ color: '#F0EDE5' }}
          >
            Two Bars. One Mission.
          </motion.h2>
        </motion.div>

        {/* Product Cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24"
        >
          {PRODUCTS.map((product, index) => (
            <motion.div
              key={product.id}
              variants={fadeUp}
              custom={index}
              className="border overflow-hidden"
              style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
              data-testid={`product-card-${product.id}`}
            >
              <div className="overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full aspect-[4/3] object-cover product-img-hover"
                  data-testid={`product-image-${product.id}`}
                />
              </div>
              <div className="p-8">
                <p className="font-accent text-xs tracking-[0.3em] uppercase mb-2" style={{ color: '#FF5A36' }}>
                  {product.tagline}
                </p>
                <h3 className="font-heading text-2xl sm:text-3xl font-light tracking-tight mb-3" style={{ color: '#F0EDE5' }}>
                  {product.name}
                </h3>
                <p className="font-body text-sm leading-relaxed" style={{ color: '#9A9A9A' }}>
                  {product.shortDesc}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Pack Pricing */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <motion.div variants={fadeUp} custom={0} className="text-center mb-12">
            <p className="font-accent text-sm tracking-[0.25em] uppercase mb-4" style={{ color: '#FF5A36' }}>
              Build Your Order
            </p>
            <h3 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mb-3" style={{ color: '#F0EDE5' }}>
              More Bars. More Savings.
            </h3>
            <p className="font-body text-base" style={{ color: '#9A9A9A' }}>
              Free shipping on every order. Mix & match any bars you want.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} custom={1} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {PACKS.map((pack) => (
              <PackCard
                key={pack.id}
                pack={pack}
                isSelected={selectedPack?.id === pack.id}
                onSelect={() => setSelectedPack(selectedPack?.id === pack.id ? null : pack)}
              />
            ))}
          </motion.div>

          {selectedPack && (
            <div className="max-w-xl mx-auto">
              <BarSelector
                key={selectedPack.id}
                pack={selectedPack}
                onAddToCart={handleAddToCart}
              />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
